import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import type { EventRate } from '../types';

interface Props {
  data: EventRate[];
}

const eventColors: Record<string, string> = {
  'boot': '#00d4ff',
  'flight_found': '#00ff88',
  'wifi_connected': '#aa66ff',
  'wifi_failed': '#ff3355',
  'ota_update': '#ffaa00',
  'esp32_timeout': '#ff3355',
};

const tooltipStyle = {
  backgroundColor: '#0c1520',
  border: '1px solid #1a2a3a',
  borderRadius: '2px',
  fontFamily: 'Share Tech Mono, monospace',
  fontSize: '11px',
};

export function EventRatesCard({ data }: Props) {
  const chartData = data.map(point => ({
    time: format(point.timestamp, 'HH:mm'),
    timestamp: point.timestamp.getTime(),
    [point.eventName]: point.count,
  }));

  const mergedData = chartData.reduce((acc, curr) => {
    const existing = acc.find(d => d.time === curr.time);
    if (existing) {
      Object.assign(existing, curr);
    } else {
      acc.push(curr);
    }
    return acc;
  }, [] as any[]);

  const eventNames = [...new Set(data.map(d => d.eventName))];

  return (
    <div className="hud-card">
      <div className="hud-corner-tl" /><div className="hud-corner-tr" /><div className="hud-corner-bl" /><div className="hud-corner-br" />
      <div className="hud-card-header">
        <div className="flex items-center gap-2.5">
          <svg className="w-4 h-4 text-hud-green opacity-80" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="10" width="2.5" height="4" fill="currentColor" opacity="0.4"/>
            <rect x="5.5" y="7" width="2.5" height="7" fill="currentColor" opacity="0.6"/>
            <rect x="9" y="4" width="2.5" height="10" fill="currentColor" opacity="0.8"/>
            <rect x="12.5" y="2" width="2.5" height="12" fill="currentColor"/>
          </svg>
          <span className="hud-card-title">Event Rates</span>
        </div>
        <span className="hud-card-badge">HOURLY</span>
      </div>
      <div className="hud-card-body">
        <div className="h-56">
          {mergedData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={mergedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#152030" />
                <XAxis
                  dataKey="time"
                  stroke="#1a2a3a"
                  tick={{ fill: '#3a5a6a', fontFamily: 'Share Tech Mono', fontSize: 10 }}
                />
                <YAxis
                  stroke="#1a2a3a"
                  tick={{ fill: '#3a5a6a', fontFamily: 'Share Tech Mono', fontSize: 10 }}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  wrapperStyle={{ fontFamily: 'Share Tech Mono', fontSize: '10px', color: '#5a7a8a' }}
                />
                {eventNames.map(eventName => (
                  <Area
                    key={eventName}
                    type="monotone"
                    dataKey={eventName}
                    stackId="1"
                    stroke={eventColors[eventName] || '#6366f1'}
                    fill={eventColors[eventName] || '#6366f1'}
                    fillOpacity={0.15}
                    strokeWidth={1.5}
                    name={eventName}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="font-mono text-sm text-[#3a5a6a]">No event data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
